import './Scene.scss';
import { utility } from '@phi/components';
import { defineComponent, h, PropType, watch } from 'vue';
import { set_class } from '@vaaas/fpts/dom';

import { Background, Environment, Lights } from '../Colours';
import {
    animate,
    refresh_all_faces,
    set_grid_size_and_adjust_grid,
} from '../scene';
import {
    create_reflection_cube_from_url_string,
    switch_to_lights,
} from '../colours_materials_appearance';
import type { State } from '../state';
import {
    activate_transparent_reference,
    deactivate_transparent_reference,
    display_hide_hidden_edges,
    display_show_hidden_edges,
} from '../ui_display';
import { Length } from '../Units';

import SelectedElements from './SelectedElements';
import FilenameState from './FilenameState';
import CurrentOperation from './CurrentOperation';
import InconsistenciesIndicator from './InconsistenciesIndicator';
import DebugIndicator from './DebugIndicator';
import { recalculate_view_dimensions } from '../utility';
import { pointer_down, pointer_move } from '../pointer';
import { mousewheel_handler } from '../pointer/mouse';
import { ENV } from '@phi/config/frontend';
import RotationLock from './RotationLock';

export default defineComponent({
    props: {
        background: { required: true, type: String as PropType<Background> },
        lights: { required: true, type: String as PropType<Lights> },
        inside_red: { required: true, type: Boolean },
        environment: { required: true, type: String as PropType<Environment> },
        display_units: { required: true, type: String as PropType<Length> },
        reference_transparent: { required: true, type: Boolean },
        hidden_edges_visible: { required: true, type: Boolean },
        wireframe_visible: { required: true, type: Boolean },
        inset: { required: true, type: Boolean },
        state: { required: true, type: Object as PropType<State> },
    },

    watch: {
        background() {
            animate();
        },

        lights(value: typeof this.lights) {
            switch_to_lights(value);
        },

        inside_red() {
            refresh_all_faces();
        },

        environment(value: typeof this.environment) {
            if (!value) return;
            create_reflection_cube_from_url_string(value);
        },

        display_units() {
            set_grid_size_and_adjust_grid(currently_selected_grid_button);
        },

        reference_transparent(value: typeof this.reference_transparent) {
            setTimeout(() => {
                if (value)
                    activate_transparent_reference();
                else
                    deactivate_transparent_reference();
            }, 200);
        },

        hidden_edges_visible(value: typeof this.hidden_edges_visible) {
            if (value)
                display_show_hidden_edges();
            else
                display_hide_hidden_edges();
        },

        wireframe_visible() {
            refresh_all_faces();
            animate();
        },
    },

    mounted() {
        this.onResize();
        window.addEventListener('resize', this.onResize);

        watch(
            () => this.inset,
            (inset) => {
                set_class(document.body, 'inset', inset);
                setTimeout(recalculate_view_dimensions, 300);
            },
            {
                immediate: true,
            });
    },

    unmounted() {
        window.removeEventListener('resize', this.onResize);
    },

    methods: {
        onResize() {
            setTimeout(() => recalculate_view_dimensions(false), 300);
        },
    },

    render() {
        return h('div', {
            class: ['phi__canvas', utility.pointer_no],
            id: 'container-parent',
            onWheel: mousewheel_handler,
        }, [
            h('div', {
                class: ['scene-container', utility.pointer_yes],
                id: 'scene-container',
                onPointermove: pointer_move,
                onPointerdown: pointer_down,
            }),
            h(SelectedElements, {
                vertices: this.state.selected_vertices_count,
                edges: this.state.selected_edges_count,
                faces: this.state.selected_faces_count,
                background: this.background,
            }),
            h(FilenameState, {
                background: this.background,
                name: this.state.current_filename,
                status: this.state.file_save_status,
            }),
            h(CurrentOperation, {
                text: this.state.current_operation_text,
                background: this.background,
            }),
            h(RotationLock, {
                text: this.state.rotation_lock_text,
                background: this.background,
            }),
            h(InconsistenciesIndicator, {
                visible: this.state.consistency_issues_found > 0,
                issues: this.state.consistency_issues_found,
            }),
            DebugIndicator({
                should_show: ENV === 'development',
                text: this.state.debug_text ?? '',
            }),
        ]);
    },
});
